import * as React from "react";
import { ActionDispatch } from "react";

import { ProjectionProvider } from "@app/projects/results/projection-provider";

import { RoleRestricted, Roles, UserContextProps, withUserContext } from "@app/user";
import { NetworkAssessmentAnalysisType, ProjectionProviderType, ProviderNetworkType } from "@app/projects/enumerators";
import ProjectService from "@app/projects/projectService";
import UpdateProjectionProvider from "@app/projects/results/projection-provider/updateProjectionProvider";
import { QESAlert, QESButton, QESMultiSelect, QESRadioButton, QESSelect, QESTextArea } from "@app/shared/qes-standards";
import { CheckboxStateEnum, QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { Accordion, AccordionContainer } from "@app/shared";
import ProjectionProviderHistoryDisplay from "@app/projects/projection-provider/ProjectionProviderHistoryDisplay";
import { MultiselectItem } from "@app/shared/qes-standards/forms/Multiselect";
import { FlyoutContextOutgoingProps, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";
import { ServicingProvider } from "@app/projects/results/servicing-provider";
import NetNewServicingProviderService from "@app/projects/servicing-provider/NetNewServicingProviderService";
import NetNewProviderHistoryDisplay from "@app/projects/net-new-provider/NetNewProviderHistoryDisplay";
import { FeatureFlagContextProps, Features, withFeatureFlagContext } from "@app/feature-flags";
import {
  ProjectionProviderDetailsAction,
  ProjectionProviderDetailsActionType,
  ProjectionProviderDetailsForm,
  ProjectionProviderIdentifier,
  ProjectionProviderStatus
} from "@app/projects/projection-provider/projectionProviderDetailsReducer";

export enum ProjectionPanelMode {
  Create = "create",
  Edit = "edit",
  View = "View",
}

interface ProjectionProviderDetailsProps {
  goBack?: () => void;
  hasProvidersWithoutNPI: boolean;
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  priorProjectionProviderProjectIds?: number[];
  priorProjectionProviderType?: ProjectionProviderType;
  projectId: number;
  //providerIds?: number[];
  selectedProviders: ServicingProvider[];
  projectionPanelMode: ProjectionPanelMode;
  /** The existing projection provider for Edit/View mode. */
  projectionProvider?: ProjectionProvider;
  projectionProviderType: ProjectionProviderType;
  providerNetworkType: ProviderNetworkType;
  /** Called after successful creation of projection providers */
  onProjectionProvidersCreated?: (runProjection: boolean, hasUnprocessedProviders?: boolean) => void;
  onProjectionProviderUpdated?: (runProjection: boolean, hasUnprocessedProviders?: boolean) => void;
  /** The specialty group code of the specialty group that these providers belong to */
  specialtyGroupCode: string;
  formState: ProjectionProviderDetailsForm;
  formDispatch: ActionDispatch<[action: ProjectionProviderDetailsAction]>;
  isZipProject: boolean;
}

interface ProjectionProviderDetailsState {
  exclusionVerifiedSourcesOptions: { [key: string]: MultiselectItem };
  reasonsForExcluding: string[];
  reasonsForTargeting: string[];
  runProjectionOnSubmit: boolean;
}

const getDefaultIdentifier = (selectedProviders: ServicingProvider[], projected: ProjectionProvider): ProjectionProviderIdentifier => {
  if (projected) {
    if (projected.projectedByNPI && projected.projectedByTIN) {
      return ProjectionProviderIdentifier.NPITIN;
    }
    if (projected.projectedByTIN) {
      return ProjectionProviderIdentifier.TIN;
    }
    if (projected.projectedByNPI) {
      return ProjectionProviderIdentifier.NPI;
    }
  }

  if (selectedProviders.findIndex((provider) => !!provider.npi) > -1) {
    return ProjectionProviderIdentifier.NPI;
  }
  if (selectedProviders.findIndex((provider) => !!provider.tin) > -1) {
    return ProjectionProviderIdentifier.TIN;
  }

  return ProjectionProviderIdentifier.None;
};

type ProjectionProviderProps =
  ProjectionProviderDetailsProps
  & UserContextProps
  & FlyoutContextOutgoingProps
  & FeatureFlagContextProps;

export class ProjectionProviderDetails extends React.Component<ProjectionProviderProps, ProjectionProviderDetailsState> {
  private readonly projectService = new ProjectService();
  private readonly netNewServicingProviderService = new NetNewServicingProviderService();

  constructor(props: ProjectionProviderProps) {
    super(props);

    const selectedOptions = this.props.projectionProvider?.exclusionVerifiedSources || [];

    this.props.formDispatch({
      payload: {
        allLocations: this.props.projectionProvider ? this.props.projectionProvider.allLocations : false,
        allProjects: this.props.projectionProvider ? this.props.projectionProvider.allProjects : false,
        allSpecialties: this.props.projectionProvider ? this.props.projectionProvider.allSpecialties : false,
        disableRerunPreview: true,
        identifier: getDefaultIdentifier(this.props.selectedProviders, this.props.projectionProvider),
        maxLength: 1000,
        reasonDescription: this.props.projectionProvider ? this.props.projectionProvider.reasonDescription : "",
        reasonName: this.props.projectionProvider ? this.props.projectionProvider.reasonName : "",
        requireReRun: false,
        selectedExclusionVerifiedSources: selectedOptions,
        status: ProjectionProviderStatus.Ready
      }, type: ProjectionProviderDetailsActionType.Initialize
    });

    this.state = {
      exclusionVerifiedSourcesOptions: this.getOptions(selectedOptions),
      reasonsForExcluding: [],
      reasonsForTargeting: [],
      runProjectionOnSubmit: true,
    };
  }

  public async componentDidMount() {
    if (this.props.projectionProviderType === ProjectionProviderType.ExcludeImpact && this.props.user.roles.indexOf(Roles.PROVIDER_EXCLUDE_USER) > -1) {
      const reasonsForExcluding = await this.projectService.getProviderExclusionReasons(this.props.projectId);
      this.setState({ reasonsForExcluding });
    }

    if (this.props.projectionProviderType === ProjectionProviderType.Target && this.props.user.roles.indexOf(Roles.PROVIDER_TARGET_USER) > -1) {
      const reasonsForTargeting = await this.projectService.getTargetProviderReasons(this.props.projectId);
      this.setState({ reasonsForTargeting });
    }
  }

  public componentDidUpdate(prevProps: ProjectionProviderDetailsProps) {
    if (prevProps.projectionProvider?.exclusionVerifiedSources !== this.props.projectionProvider?.exclusionVerifiedSources ||
      prevProps.formState.selectedExclusionVerifiedSources !== this.props.formState.selectedExclusionVerifiedSources) {
      this.setState({
        exclusionVerifiedSourcesOptions: this.getOptions(),
      });
    }
  }

  public render() {
    const createMode = this.props.projectionPanelMode === ProjectionPanelMode.Create;
    const editMode = this.props.projectionPanelMode === ProjectionPanelMode.Edit;
    const viewMode = this.props.projectionPanelMode === ProjectionPanelMode.View;
    const disabled = !this.state || (this.props.formState.status !== ProjectionProviderStatus.Ready && this.props.formState.status !== ProjectionProviderStatus.CanSubmit) || viewMode;
    const canSubmit = this.state && this.props.formState.status === ProjectionProviderStatus.CanSubmit && !viewMode;

    const showProgress = this.props.formState.status !== ProjectionProviderStatus.Ready && this.props.formState.status !== ProjectionProviderStatus.CanSubmit;
    const hasProvidersWithoutNPI = this.props.hasProvidersWithoutNPI;

    const tinViewer = this.props.user.roles.indexOf(Roles.TIN_VIEWER) > -1;
    const targetExcludeByNameFeatureEnabled = this.props.enabledFeatureFlags.has(Features.TARGET_EXCLUDE_BY_NAME);

    // Disable checkboxes if matching by name is enabled and there are any selected providers without an NPI or TIN (if applicable).
    const matchOnName = targetExcludeByNameFeatureEnabled &&
      this.props.selectedProviders.some((provider) => !provider.npi && (!tinViewer || !provider.tin))

    //filter out providers without an NPI AND TIN when determining what recording options to display
    const providersWithTinOrNPI = this.props.selectedProviders.filter((provider) => !!provider.npi || !!provider.tin)

    const showNpi = editMode ? this.props.projectionProvider.npi != null : providersWithTinOrNPI.length > 0 && providersWithTinOrNPI.every((provider) => !!provider.npi);
    const showTin = tinViewer && editMode ? this.props.projectionProvider.tin != null : providersWithTinOrNPI.length > 0 && providersWithTinOrNPI.every((provider) => !!provider.tin);

    const checkboxDisabled = viewMode || editMode;
    return (
      <>
        {(hasProvidersWithoutNPI && !targetExcludeByNameFeatureEnabled) && (
          <QESAlert type="warning">
            Any Providers selected that do not have an NPI, will be removed from the list once the prospect/market data
            is refreshed with new data.

            Additional options (such as All Projects/All Locations/All Specialties) may not be available due to one or
            more missing NPIs.
          </QESAlert>
        )}
        {this.showWarningBanner() &&
          (<QESAlert type="warning" persist={true}>
            <div>
              This status will require additional supporting documentation to be included with CMS exception documents.
            </div>
          </QESAlert>)}
        {createMode && (<>
          {this.props.projectionProviderType !== ProjectionProviderType.Target &&
            <>
              <p
                className="lead">{this.projectionTypeInfinitiveTense}{` Provider${this.props.selectedProviders.length > 1 ? "s" : ""} `}</p>
              <hr/>
            </>
          }
          {/* Progress messages */}
          <div>
            {(showProgress || null) && (
              <>
                {(this.props.formState.status === ProjectionProviderStatus.Creating || null) && (
                  <QESAlert type="info" persist={true}><i className="fa fa-spinner fa-spin"/> Processing
                    request...</QESAlert>
                )}
                {(this.props.formState.status === ProjectionProviderStatus.Finished || null) && (
                  <QESAlert type="success" persist={true}>Success! Analysis complete.</QESAlert>
                )}
                {(this.props.formState.status === ProjectionProviderStatus.Error && createMode) && (
                  <QESAlert type="danger" persist={true}>Failed
                    to {this.projectionTypeInfinitiveTense.toLowerCase()} the provider(s). Review your selections and
                    try again.</QESAlert>
                )}
                {(this.props.formState.status === ProjectionProviderStatus.Error && editMode) && (
                  <QESAlert type="danger">Failed to update. Review your selections and try again.</QESAlert>
                )}
              </>
            )}
          </div>
          <p>
            Please enter the additional information to
            <strong>
              {` ${this.projectionTypeInfinitiveTense.toLowerCase()}`}
              {` ${this.props.selectedProviders.length}`}
              {` provider${this.props.selectedProviders.length > 1 ? "s" : ""} `}
            </strong>
            from the
            <strong>
              {` ${this.providerNetworkTypeDisplay.toLowerCase()} `}
            </strong>
            network...
          </p>
        </>)}
        {editMode && (
          <p>Edit {this.props.projectionProviderType === ProjectionProviderType.ExcludeImpact ? "Excluded" : "Targeted"} Provider
            Information</p>
        )}
        {viewMode && (
          <p>View {this.props.projectionProviderType === ProjectionProviderType.ExcludeImpact ? "Excluded" : "Targeted"} Provider
            Information</p>
        )}

        {this.props.projectionProviderType === ProjectionProviderType.ExcludeImpact && (
          <div className="row">
            <div className="col-8 mb-3">
              <QESMultiSelect
                label="Exclusion Verified Sources"
                options={this.state.exclusionVerifiedSourcesOptions}
                testId="projection-flyout__source"
                onChange={e => this.props.formDispatch({
                  payload: e.checkedKeys,
                  type: ProjectionProviderDetailsActionType.SelectedExclusionVerifiedSources
                })}
                disabled={disabled}
              />
            </div>
          </div>
        )}


        {this.props.providerNetworkType != ProviderNetworkType.NetNew && (<div className="mb-3">
          <div className="row col-4">
            <QESCheckbox
              label="All Locations"
              checked={this.props.formState.allLocations}
              disabled={disabled || hasProvidersWithoutNPI === true || matchOnName === true}
              onChange={(e) => this.props.formDispatch({
                payload: e.target.checked,
                type: ProjectionProviderDetailsActionType.AllLocations
              })}
              testId="projection-flyout__all-locations"
            />
          </div>


          {(this.isProviderType(ProjectionProviderType.ExcludeImpact) || null) && (
            <div className="row col-4">
              <QESCheckbox
                checked={this.props.formState.allProjects}
                label="All Projects"
                disabled={disabled || hasProvidersWithoutNPI === true || matchOnName === true}
                onChange={(e) => this.props.formDispatch({
                  payload: e.target.checked,
                  type: ProjectionProviderDetailsActionType.AllProjects
                })}
                testId="projection-flyout__all-projects"
              />
            </div>
          )}

          <div className="row col-4">
            <QESCheckbox
              label="All Specialties"
              checked={this.props.formState.allSpecialties}
              disabled={disabled || hasProvidersWithoutNPI === true || matchOnName === true}
              onChange={(e) => this.props.formDispatch({
                payload: e.target.checked,
                type: ProjectionProviderDetailsActionType.AllSpecialties
              })}
              testId="projection-flyout__all-specialties"
            />
          </div>
        </div>)}

        {(showNpi || showTin) &&
          <div>
            <div className="row">
              <label className="col-12 label-s d-block mb-2" htmlFor="reason">Recording Criteria:</label>
            </div>
            <div className="row">
              <div className="col-12 ml-4">
                {showNpi && (<div>
                  <QESRadioButton
                    label="NPI"
                    checked={this.props.formState.identifier === "npi"}
                    disabled={checkboxDisabled}
                    name="identifier"
                    onChange={(e) => this.props.formDispatch({
                      payload: e.target.value as ProjectionProviderIdentifier,
                      type: ProjectionProviderDetailsActionType.Identifier
                    })}
                    value="npi"
                    testId="termination-flyout__by-npi"
                  />
                </div>)}

                {showTin && (<div>
                  <QESRadioButton
                    label="TIN"
                    checked={this.props.formState.identifier === "tin"}
                    disabled={checkboxDisabled}
                    name="identifier"
                    onChange={(e) => this.props.formDispatch({
                      payload: e.target.value as ProjectionProviderIdentifier,
                      type: ProjectionProviderDetailsActionType.Identifier
                    })}
                    value="tin"
                    testId="termination-flyout__by-tin"
                  />
                </div>)}

                {showNpi && showTin && (<div>
                  <QESRadioButton
                    label="NPI and TIN"
                    checked={this.props.formState.identifier === "npitin"}
                    disabled={checkboxDisabled}
                    name="identifier"
                    onChange={(e) => this.props.formDispatch({
                      payload: e.target.value as ProjectionProviderIdentifier,
                      type: ProjectionProviderDetailsActionType.Identifier
                    })}
                    value="npitin"
                    testId="termination-flyout__by-npi-and-tin"
                  />
                </div>)}
              </div>
            </div>
          </div>
        }


        <div className="row mb-3 mt-4">
          <div className="col-12">
            {this.isProviderType(ProjectionProviderType.Target) && <p>The Reason and Comment entered below will be visible to any other users within your organization.</p>}
            {this.isProviderType(ProjectionProviderType.ExcludeImpact) && <p>Would you like to process this projection so that the results are saved and shared with other users?</p>}
          </div>
        </div>

        <div className="row mb-3 col-6">
          <QESSelect
            label="Reason"
            disabled={disabled}
            testId="projection-flyout__reason"
            onChange={e => this.props.formDispatch({
              payload: e.target.value,
              type: ProjectionProviderDetailsActionType.ReasonName
            })}
            value={this.props.formState.reasonName}
            required={true}
            isValid={!!this.props.formState.reasonName}
            placeholder={`Reason for ${this.projectionTypePresentTense.toLowerCase()}`}
          >
            {viewMode ? (
              <option value={this.props.formState.reasonName}>{this.props.formState.reasonName}</option>
            ) : (
              <>
                {this.isProviderType(ProjectionProviderType.Target) && this.state.reasonsForTargeting.map((o, i) => (
                  <option key={i + 1} value={o}>{o}</option>
                ))}
                {this.isProviderType(ProjectionProviderType.ExcludeImpact) && this.state.reasonsForExcluding.map((o, i) => (
                  <option key={i + 1} value={o}>{o}</option>
                ))}
              </>
            )}
          </QESSelect>
        </div>

        <div className="row mb-3">
          <QESTextArea
            containerClassName="col-6"
            disabled={disabled}
            onChange={e => this.props.formDispatch({
              payload: e.target.value,
              type: ProjectionProviderDetailsActionType.ReasonDescription
            })}
            value={this.props.formState.reasonDescription}
            maxLength={this.props.formState.maxLength}
            label="Comment"
            testId="projection-flyout__comment"
            required={this.props.formState.reasonName === "Other"}
          />
        </div>

        <RoleRestricted allowedRoles={[Roles.PROJECTION_PROCESSOR]}>
          <div className="row mb-3">
            {(createMode || (editMode && this.props.formState.requireReRun)) &&
              (
                <div className="col-4">
                  <QESCheckbox
                    label="Run Projection Analysis Later"
                    testId="projection-flyout__run-later"
                    checked={!this.state.runProjectionOnSubmit}
                    disabled={disabled}
                    onChange={(e) => {
                      this.setState({ runProjectionOnSubmit: !e.currentTarget.checked });
                    }}
                  />
                </div>
              )
            }
          </div>
        </RoleRestricted>

        {editMode &&
          <AccordionContainer>
            <Accordion
              title="History"
              testId="projection-provider-detail__history-accordion"
            >
              {this.props.providerNetworkType == ProviderNetworkType.NetNew ?
                <NetNewProviderHistoryDisplay netNewProviderProjectId={this.props.projectionProvider.providerId}/> :
                <ProjectionProviderHistoryDisplay
                  projectionProviderProjectId={this.props.projectionProvider.projectionProviderProjectId}
                  projectionProviderSettingsId={this.props.projectionProvider.projectionProviderSettingsId}/>}
            </Accordion>
          </AccordionContainer>
        }

        <FlyoutFooter
          submitButton={
            <div>
              {this.props.projectionProviderType === ProjectionProviderType.Target && !!this.props.goBack && (
                <QESButton className="mr-3" qesType="secondary-danger" onClick={this.props.goBack}
                           qesTestId="project-provider-details__go-back">Previous</QESButton>
              )}
              <QESButton disabled={!canSubmit} onClick={this.handleSubmit} qesTestId="projections-flyout__submit"
                         qesType="primary">Submit</QESButton>
            </div>
          }
        />
      </>
    );
  }

  private showWarningBanner = () => {
    return this.props.formState.reasonName === "Military Base" || this.props.formState.reasonName === "Provider Does not Contract with any MCO (Managed Care Organization)"
      || this.props.formState.reasonName === "Exclusive Agreement";
  }

  private getOptions = (selectedOptions?: string[]) => {
    const currentSelections = selectedOptions || this.props.formState.selectedExclusionVerifiedSources;
    /* eslint-disable sort-keys */
    const keys = [
      "Physician Compare",
      "Hospital Compare",
      "Nursing Home Compare",
      "Dialysis Compare",
      "NPI file/NPPES",
      "Provider of Services (POS) Files",
      "Direct outreach to provider",
      "Provider Website",
      "State Licensing Data",
      "Online Mapping Tool",
      "CMS MA Supply File",
      "CMS MMP Supply File",
      "Other"
    ];
    const options: { [key: string]: MultiselectItem } = {};
    keys.forEach((k) => {
      options[k] = {
        currentState: currentSelections.find((sevs) => sevs === k) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
        displayValue: k,
      };
    });
    return options;
    /* eslint-enable sort-keys */
  }

  private handleSubmit = async () => {
    if (!this.props.isZipProject) {
      if (this.props.formState.status === ProjectionProviderStatus.CanSubmit) {
        // set the state to indicate we're beginning the 'create' process
        this.setState((preCreateState) => ({ ...preCreateState, status: ProjectionProviderStatus.Creating }), async () => {
          try {
            if (this.props.projectionPanelMode === ProjectionPanelMode.Edit) {
              this.props.providerNetworkType != ProviderNetworkType.NetNew ?
                await this.projectService.updateProjectionProvider(this.props.projectId,
                  {
                    allLocations: this.props.formState.allLocations,
                    allProjects: this.props.formState.allProjects,
                    allSpecialties: this.props.formState.allSpecialties,
                    exclusionVerifiedSources: this.props.formState.selectedExclusionVerifiedSources,
                    projectByNPI: this.props.formState.identifier.includes("npi"),
                    projectByTIN: this.props.formState.identifier.includes("tin"),
                    projectionProviderProjectId: this.props.projectionProvider.projectionProviderProjectId,
                    projectionProviderSettingsId: this.props.projectionProvider.projectionProviderSettingsId,
                    projectionProviderType: this.props.projectionProvider.projectionType,
                    providerNetworkType: this.props.providerNetworkType,
                    reasonDescription: this.props.formState.reasonDescription,
                    reasonName: this.props.formState.reasonName,
                    specialtyGroupCode: this.props.specialtyGroupCode,
                  } as UpdateProjectionProvider) :
                await this.netNewServicingProviderService.updateNetNewReasonNameAndDescription(
                  this.props.formState.reasonName,
                  this.props.formState.reasonDescription,
                  [this.props.projectionProvider.providerId]);

              this.props.formDispatch({ type: ProjectionProviderDetailsActionType.Finished });
              this.props.onProjectionProviderUpdated(this.state.runProjectionOnSubmit && this.props.formState.requireReRun, this.props.formState.requireReRun);
              this.props.handleCloseFlyout().then();
            } else {
              if (this.props.priorProjectionProviderType) {
                //changing projection provider type. Remove the old projection providers then
                //re-add with the new type
                // attempt to remove the projection providers
                await this.projectService.deleteProjectionProviders(this.props.projectId, this.props.priorProjectionProviderProjectIds);
              }

              // attempt to create the projection providers
              if (this.props.providerNetworkType != ProviderNetworkType.NetNew) {
                await this.projectService.createProjectionProviders(
                  this.props.projectId,
                  this.props.providerNetworkType,
                  this.props.projectionProviderType,
                  this.props.formState.identifier.includes("npi"),
                  this.props.formState.identifier.includes("tin"),
                  this.props.selectedProviders.map((provider) => provider.providerId),
                  this.props.formState.reasonName,
                  this.props.formState.reasonDescription,
                  this.props.formState.allSpecialties,
                  this.props.formState.allLocations,
                  this.props.formState.allProjects,
                  this.props.specialtyGroupCode,
                  this.props.formState.selectedExclusionVerifiedSources,
                )
              } else {
                await this.netNewServicingProviderService.updateNetNewIsTargeted(true, this.props.selectedProviders.map(nnp => nnp.providerId));
                await this.netNewServicingProviderService.updateNetNewReasonNameAndDescription(this.props.formState.reasonName,
                  this.props.formState.reasonDescription, this.props.selectedProviders.map(nnp => nnp.providerId));
              }

              // set the state to indicate we've successfully finished the 'create' process
              this.props.formDispatch({ type: ProjectionProviderDetailsActionType.Finished });
              // notify the consuming component that the projection providers have been created
              this.props.onProjectionProvidersCreated(this.state.runProjectionOnSubmit);
              this.props.handleCloseFlyout().then();
            }

          } catch (e) {
            // we failed to create the projection providers
            this.setState((prevState) => ({
              ...prevState,
              status: ProjectionProviderStatus.Error,
            }));
          }
        });
      }
    }
    else {
      //TODO: Connect to zip targeting code once implemented in backend
      this.props.handleCloseFlyout().then();
    }
  }

  private isProviderType(projectionProviderType: ProjectionProviderType): boolean {
    return this.props.projectionProviderType === projectionProviderType;
  }

  private get projectionTypeInfinitiveTense(): string {
    switch (this.props.projectionProviderType) {
      case ProjectionProviderType.ExcludeImpact:
        return "Exclude";
      case ProjectionProviderType.Target:
        return "Target";
      case ProjectionProviderType.Unknown:
        return "Unknown";
    }
  }

  private get projectionTypePresentTense(): string {
    switch (this.props.projectionProviderType) {
      case ProjectionProviderType.ExcludeImpact:
        return "Excluding";
      case ProjectionProviderType.Target:
        return "Targeting";
      case ProjectionProviderType.Unknown:
        return "Unknown";
    }
  }

  private get providerNetworkTypeDisplay(): string {
    switch (this.props.providerNetworkType) {
      case ProviderNetworkType.Market:
        return "Market";
      case ProviderNetworkType.Network:
        return "Network";
      case ProviderNetworkType.Prospect:
        return "Prospect";
      default:
        return "";
    }
  }
}

const
  WrappedProjectionProviderDetails = withFeatureFlagContext(withFlyoutContext(withUserContext(ProjectionProviderDetails)));
export {
  WrappedProjectionProviderDetails
};
